/**
 * Shared chrome for settings sections: section header, group headings,
 * loading placeholder, the field label and the paragraph-shaped setting.
 * Every control commits itself (DESIGN.md, Save model).
 *
 * A section is one screen of groups, with the one exception the
 * drill-down row and the back link exist for: Phone opens the
 * Receptionist. Settings held rooms inside rooms once — Intake, then Phone,
 * then a Receptionist config that held a Knowledge, a switch four clicks
 * from a sidebar that was already the navigation. What is left of that is
 * a single step, taken from the screen the Company is already reading.
 */
import type { ReactNode } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../components/ui/icons";
import { InlineBanner } from "../../components/ui/InlineBanner";
import { Tooltip } from "../../components/ui/Tooltip";
import { useSettingCommit } from "../../lib/use-setting-commit";

import styles from "./section-chrome.module.css";

/**
 * Field label. An explanation hangs off the label text itself — there is
 * no info glyph beside it (DESIGN.md, Tooltips).
 */
export function FieldLabel({
  htmlFor,
  tooltip,
  children,
}: {
  htmlFor: string;
  /** One sentence explaining what the field's value affects. */
  tooltip?: string;
  children: React.ReactNode;
}) {
  // The Tooltip wraps the label rather than sitting inside it: Astryx keeps the
  // (closed) bubble in the DOM, and inside the label its text would join the
  // field's accessible name.
  const label = (
    <label className={styles.fieldLabel} htmlFor={htmlFor}>
      {children}
    </label>
  );
  // inline-block on the trigger: it wraps a block-level <label>, and an inline
  // span around a block child has no usable box of its own — Astryx measures
  // that empty rect and lands the bubble away from the label it describes.
  return tooltip ? (
    <Tooltip content={tooltip} className={styles.tooltipTrigger}>
      {label}
    </Tooltip>
  ) : (
    label
  );
}

/**
 * A paragraph-shaped setting: label above, field full width beneath it.
 *
 * DESIGN.md's setting row puts the control on the right, which is the
 * right shape for a switch, a short address, or a number. It is the
 * wrong shape for the three settings that are prose — a greeting a caller
 * hears, a voicemail prompt, and the notes the Receptionist may answer
 * from.
 * Those get the studio treatment instead ("Inline values edit beside the
 * artifact, as fields"): a full-width field with room to read the whole
 * value, still committing on blur and still showing its failure on
 * itself.
 */
export function TextAreaField({
  id,
  label,
  description,
  tooltip,
  value,
  onSave,
  rows = 4,
  children,
  replaced = false,
}: {
  id: string;
  label: string;
  /** One line under the label, read before the value is typed. */
  description?: string;
  /** One sentence on what the value affects; hangs off the label. */
  tooltip?: string;
  value: string;
  onSave: (next: string) => Promise<void>;
  rows?: number;
  /** What belongs to the field under the box: a recording of the words. */
  children?: ReactNode;
  /**
   * The box is not drawn: what stands in for the words (a recording) is
   * all the field shows, rather than a box beside a note saying the box
   * is not what is used.
   */
  replaced?: boolean;
}) {
  const { draft, setDraft, onFocus, saving, error, commitDraft } =
    useSettingCommit(value, onSave);

  return (
    <div className={styles.field}>
      <FieldLabel htmlFor={id} tooltip={tooltip}>
        {label}
      </FieldLabel>
      {description && (
        <p className={styles.fieldDescription}>{description}</p>
      )}
      {!replaced && (
        <textarea
          id={id}
          rows={rows}
          className={styles.textarea}
          value={draft}
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={onFocus}
          onBlur={() => void commitDraft()}
        />
      )}
      {error && <InlineBanner variant="error">{error}</InlineBanner>}
      {children}
    </div>
  );
}

export function Loading() {
  return <p className={styles.loading}>Loading…</p>;
}

/**
 * A row that opens its own space: title, one-line description, right
 * chevron. The drill-down gallery pattern from Documents & emails — a
 * subject a section carries but does not read as one of its own groups
 * names its destination instead of unrolling into it.
 */
/**
 * The rows that open a page, as one list: they sit where a group would,
 * with no heading of their own — a short list of named rows does not need
 * a name and a description above it saying the same thing twice.
 */
export function DrillList({ children }: { children: React.ReactNode }) {
  return <ul className={styles.drillList}>{children}</ul>;
}

export function DrillDownRow({
  name,
  description,
  onOpen,
}: {
  name: string;
  description?: string;
  onOpen: () => void;
}) {
  return (
    <li className={styles.drillItem}>
      <button type="button" onClick={onOpen} className={styles.drillRow}>
        <span className={styles.drillText}>
          <span className={styles.drillName}>{name}</span>
          {description && (
            <span className={styles.drillDescription}>{description}</span>
          )}
        </span>
        <ChevronRightIcon className={styles.drillChevron} />
      </button>
    </li>
  );
}

/** The back link above a drilled-into space. */
export function BackLink({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles.backLink}
    >
      <ChevronLeftIcon className={styles.backIcon} />
      {label}
    </button>
  );
}

export function SectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div className={styles.sectionHeader}>
      <div className={styles.sectionHeaderText}>
        <h2 className={styles.sectionTitle}>{title}</h2>
        <p className={styles.sectionDescription}>{description}</p>
      </div>
      {action}
    </div>
  );
}

export function SettingsGroup({
  id,
  title,
  titleText,
  description,
  action,
  children,
}: {
  id: string;
  /** Text, or a control where the heading itself is editable. */
  title: React.ReactNode;
  /**
   * The section's name when `title` is a control. An <h3> wrapping an
   * input has no text of its own, so `aria-labelledby` would name the
   * section nothing.
   */
  titleText?: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-labelledby={titleText ? undefined : id}
      aria-label={titleText}
      className={styles.group}
    >
      <div className={styles.groupHeader}>
        <div className={styles.groupHeaderText}>
          <h3 id={id} className={styles.groupTitle}>
            {title}
          </h3>
          {description && (
            <p className={styles.groupDescription}>{description}</p>
          )}
        </div>
        {action}
      </div>
      <div className={styles.groupBody}>{children}</div>
    </section>
  );
}
